import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Loader2, Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import { PasswordStrengthHint } from '../components/PasswordStrengthHint';
import { checkPasswordStrength, PASSWORD_POLICY_MESSAGE } from '../utils/passwordStrength';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_myzow-system/artifacts/qvuxw5eo_myzow-logo-red.png';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  // Token validation state: null = checking, true = valid, false = invalid
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenIssue, setTokenIssue] = useState({ reason: '', message: '' });

  useEffect(() => { document.title = 'Reset Password | Myzow ERP 2.0'; }, []);

  // Validate the token on mount so we can show a clear "already used" /
  // "expired" / "invalid" message instead of the password form.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenValid(false);
      setTokenIssue({ reason: 'invalid', message: 'This password reset link is invalid. Please request a new one.' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenIssue({ reason: data.reason || 'invalid', message: data.message || 'This password reset link is no longer valid.' });
        }
      } catch {
        if (!cancelled) {
          setTokenValid(false);
          setTokenIssue({ reason: 'network', message: 'Could not verify the reset link. Please try again.' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!checkPasswordStrength(password).valid) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        // Backend returns 410 on used/expired with a clear `detail`.
        // If the token has gone stale between page load and submit, swap to the stale-link screen.
        if (res.status === 410) {
          setTokenValid(false);
          setTokenIssue({ reason: 'used', message: data.detail || 'This password reset link is no longer valid.' });
        } else {
          setError(data.detail || 'Failed to reset password');
        }
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Token-state screens (shown before the password form)
  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md glass animate-fade-in" data-testid="reset-token-checking">
          <CardContent className="pt-6 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">Validating your reset link…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValid === false) {
    const titleByReason = {
      used: 'Link Already Used',
      expired: 'Link Expired',
      invalid: 'Invalid Reset Link',
      network: 'Could Not Verify Link',
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md glass animate-fade-in" data-testid={`reset-token-${tokenIssue.reason || 'invalid'}`}>
          <CardContent className="pt-6 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <KeyRound className="h-8 w-8 text-destructive" />
            </div>
            <p className="text-lg font-semibold">{titleByReason[tokenIssue.reason] || 'Invalid Reset Link'}</p>
            <p className="text-sm text-muted-foreground">{tokenIssue.message}</p>
            <Button onClick={() => navigate('/forgot-password')} className="w-full" data-testid="request-new-link">Request a New Link</Button>
            <Button variant="ghost" className="w-full text-sm" onClick={() => navigate('/login')} type="button">
              <ArrowLeft className="h-4 w-4 mr-2" />Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md glass animate-fade-in relative z-10" data-testid="reset-password-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={LOGO_URL} alt="Myzow" className="h-12 object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">{success ? 'Password Reset' : 'Set New Password'}</CardTitle>
            <CardDescription>
              {success ? 'Your password has been reset successfully.' : 'Enter your new password below.'}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground text-center">You can now sign in with your new password.</p>
              <Button className="w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 chars · letter, number, symbol"
                    required
                    minLength={8}
                    disabled={loading}
                    className="bg-muted/50 pr-10"
                    data-testid="reset-new-password"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrengthHint password={password} />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  disabled={loading}
                  className="bg-muted/50"
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</>) : 'Reset Password'}
              </Button>
              <Button variant="ghost" className="w-full text-sm" onClick={() => navigate('/login')} type="button">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
